/*
 * exchange-session.js — ANSIN "wymiana" (exchange) storefront session carrier.
 *
 * Captures the exchange context handed off from the customer-account extension
 * via URL params (?ex=1&ex_order&ex_variant&ex_deadline) and persists it into
 * hidden `_exchange_*` cart attributes so it rides cart -> checkout -> order
 * (order.note_attributes).
 *
 * STORAGE MODEL (three layers, deliberately):
 *   - cart attributes  — the CARRIER OF RECORD. Cookie-backed, browser-wide, and the
 *                        only thing the Function and the cart bar actually read.
 *   - sessionStorage   — per-TAB mirror, used to re-apply the marker if the cart drops
 *                        it. Narrower than the cart, so it is derived, never truth.
 *   - localStorage hint— browser-wide "this browser touched the exchange flow" flag, so
 *                        a fresh tab knows to consult the cart without making every
 *                        ordinary visitor pay a /cart.js fetch on every page view.
 *
 * Cross-phase contract (Phase 3 gates on _exchange_session; Phase 4 derives
 * wymiana_z_#<_exchange_original_order>): keep these key names in sync with
 * .planning .../02-01-PLAN.md "Cross-Phase Contract".
 *
 * DEBUG: append ?ex_debug=1 to any URL (sticky via localStorage — survives the
 * URL scrub and later page loads) to log every capture / write / reconcile /
 * lapse decision, including the landing URL. Turn off with ?ex_debug=0.
 *
 * Classic (non-module) global IIFE — loaded via <script src defer>. No import/export.
 */
(() => {
  "use strict";

  const SS_KEY = "ansin_exchange";
  const LS_HINT = "ansin_exchange_hint";
  // Token of the cart this session was stamped onto. localStorage, not the per-tab
  // mirror: the customer checks out and comes back in whatever tab she likes, and the
  // cart is browser-wide, so the anchor has to be too. Kept in the mirror as well, as
  // a fallback for when localStorage is unavailable.
  const LS_CART = "ansin_exchange_cart";
  const DEBUG_KEY = "ansin_exchange_debug";

  // Theme-portable cart endpoints. Prefer the theme's own routes (locale-aware);
  // fall back to the platform-standard AJAX endpoints so this asset drops into
  // any theme (Dawn, Horizon, Debut, ...) without depending on theme globals.
  const routes = (typeof window !== "undefined" && window.routes) || {
    cart_url: "/cart",
    cart_update_url: "/cart/update.js",
  };

  // Prefer the theme's fetchConfig (Dawn/ANSIN global) when present; otherwise a
  // plain JSON POST config.
  const postConfig = () =>
    typeof fetchConfig === "function"
      ? fetchConfig()
      : { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" } };

  // --- Store-local clock --------------------------------------------------
  // The free-shipping Function can only read the STORE's local wall clock — Shopify
  // Functions have no clock and no timezone database — so the deadline has to reach
  // the cart in store-local time as well as epoch ms. layout/theme.liquid renders the
  // same instant three ways; the difference gives us the shop's UTC offset.
  //
  // Only the OFFSET is used. `Date.now()` stays the source of "now", so a cached page
  // carrying a stale epoch_ms is harmless (offsets are stable except across DST).
  const shopUtcOffsetMs = (() => {
    const t = (typeof window !== "undefined" && window.theme && window.theme.shop_time) || null;
    if (!t) return null;

    // Preferred: "+0200" / "+02:00".
    const zone = /^([+-])(\d{2}):?(\d{2})$/.exec(String(t.utc_offset ?? "").trim());
    if (zone) {
      const sign = zone[1] === "-" ? -1 : 1;
      return sign * (Number(zone[2]) * 60 + Number(zone[3])) * 60000;
    }

    // Fallback: the same instant as epoch ms and as a zone-less local ISO string.
    const local = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(String(t.local_iso ?? ""));
    const epochMs = Number(t.epoch_ms);
    if (!local || !Number.isFinite(epochMs)) return null;
    // Date.parse on a zone-less ISO string is BROWSER-local — never use it here.
    const localAsUtc = Date.UTC(+local[1], +local[2] - 1, +local[3], +local[4], +local[5], +local[6]);
    // Render/transfer skew is sub-second; real offsets are whole minutes.
    return Math.round((localAsUtc - epochMs) / 60000) * 60000;
  })();

  // The free-shipping Function reads ONLY `_exchange_deadline_local` (its input query
  // is capped at a cost of 30 and the hourly clock probes eat most of it), so if the
  // offset cannot be derived there is no free shipping at all. That is a theme
  // misconfiguration, not a customer scenario — say so loudly rather than letting it
  // look like a broken discount.
  if (shopUtcOffsetMs === null) {
    console.warn(
      "[exchange-session] window.theme.shop_time is missing or unparseable — the " +
        "exchange deadline cannot be converted to shop-local time, so the " +
        "free-shipping Function will not grant. Check the shop_time block in " +
        "layout/theme.liquid."
    );
  }

  // Shift the instant by the shop's offset, then read UTC fields — that yields the
  // shop's wall-clock fields. Returns null when the offset is unknown; we omit the
  // attribute rather than guess, because guessing the shop's timezone from the
  // browser's would silently grant or refuse free shipping by up to a day.
  const toShopLocalIso = (utcMs) => {
    if (shopUtcOffsetMs === null || !Number.isFinite(utcMs)) return null;
    return new Date(utcMs + shopUtcOffsetMs).toISOString().slice(0, 19);
  };

  // Optional theme surface (the ANSIN announcement bar). Guarded exactly like
  // updateFreeShipping() so this shared file stays a no-op on themes without it.
  const syncExchangeBar = (deadlineMs) => {
    try {
      if (window.ANSIN_EXCHANGE_BAR) window.ANSIN_EXCHANGE_BAR.sync(deadlineMs ?? null);
    } catch { /* non-fatal */ }
  };

  // --- Debug mode ---------------------------------------------------------
  const DEBUG = (() => {
    try {
      const q = new URLSearchParams(location.search);
      if (q.get("ex_debug") === "1") {
        try { localStorage.setItem(DEBUG_KEY, "1"); } catch { /* private mode */ }
        return true;
      }
      if (q.get("ex_debug") === "0") {
        try { localStorage.removeItem(DEBUG_KEY); } catch { /* private mode */ }
        return false;
      }
      if (window?.ANSIN_EXCHANGE_DEBUG === true) return true;
      return localStorage.getItem(DEBUG_KEY) === "1";
    } catch {
      return false;
    }
  })();

  const log = (...args) => {
    if (!DEBUG) return;
    try { console.log("[exchange-session]", ...args); } catch { /* noop */ }
  };

  log("loaded on", location.href);

  // --- Storage helpers ----------------------------------------------------
  const getStoredExchange = () => {
    try {
      const raw = sessionStorage.getItem(SS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const clearStoredExchange = () => {
    try { sessionStorage.removeItem(SS_KEY); } catch { /* private mode */ }
  };

  const setHint = () => {
    try { localStorage.setItem(LS_HINT, "1"); } catch { /* private mode */ }
  };

  const clearHint = () => {
    try { localStorage.removeItem(LS_HINT); } catch { /* private mode */ }
  };

  const hasHint = () => {
    try { return localStorage.getItem(LS_HINT) === "1"; } catch { return false; }
  };

  const setAnchor = (token) => {
    if (!token) return;
    try { localStorage.setItem(LS_CART, token); } catch { /* private mode */ }
  };

  const getAnchor = () => {
    try { return localStorage.getItem(LS_CART); } catch { return null; }
  };

  const clearAnchor = () => {
    try { localStorage.removeItem(LS_CART); } catch { /* private mode */ }
  };

  // Is the CART — the carrier of record — carrying an active, unexpired session?
  // Mirrors the Function's gate (session flag AND unexpired) so the storefront never
  // promises a benefit checkout will refuse.
  const cartSessionActive = (attrs) => {
    const deadlineMs = Number(attrs?._exchange_deadline);
    return attrs?._exchange_session === "1" && Number.isFinite(deadlineMs) && Date.now() < deadlineMs;
  };

  // --- Bar repaint (Section Rendering) ------------------------------------
  // The exchange bar in the Dawn port (exchange-shipping-bar.liquid) is
  // SERVER-RENDERED and only reflects the _exchange_* attributes as they stood
  // at the last section render. When we (re-)write the marker from JS — e.g. the
  // "marker missing — re-applying" path after adding the first item to an empty
  // cart, whose add-to-cart render happened before the attribute existed — the
  // already-rendered drawer/cart keeps showing the normal bar until a hard
  // refresh. Fix: request the cart sections in the SAME /cart/update.js write and
  // swap the fresh HTML in, exactly like the theme's own cart.js/product-form.js.
  //
  // Keyed off the port's own [data-exchange-shipping-bar] marker, so it is a
  // strict no-op on the ANSIN theme (which has no such element and repaints its
  // <shipping-bar> imperatively via updateFreeShipping() instead — called below).
  function exchangeSectionTargets() {
    const targets = [];
    const seen = new Set();
    document.querySelectorAll("[data-exchange-shipping-bar]").forEach((bar) => {
      // Cart drawer: re-render the 'cart-drawer' section, swap its inner.
      if (bar.closest("#CartDrawer")) {
        if (!seen.has("CartDrawer")) {
          seen.add("CartDrawer");
          targets.push({ id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" });
        }
        return;
      }
      // Cart page: the enclosing Shopify section (e.g. main-cart-footer).
      const sec = bar.closest(".shopify-section");
      if (sec?.id && !seen.has(sec.id)) {
        seen.add(sec.id);
        targets.push({ id: sec.id, section: sec.id.replace(/^shopify-section-/, ""), selector: ".js-contents" });
      }
    });
    return targets;
  }

  function sectionInnerHTML(html, selector) {
    try {
      const el = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
      return el ? el.innerHTML : null;
    } catch {
      return null;
    }
  }

  function injectSections(sections, targets) {
    targets.forEach((t) => {
      const html = sections?.[t.section];
      if (!html) return;
      const dest = document.getElementById(t.id)?.querySelector(t.selector);
      const inner = sectionInnerHTML(html, t.selector);
      if (dest && inner != null) dest.innerHTML = inner;
    });
  }

  // ANSIN's imperative bar (<shipping-bar>) has no server section to swap; repaint
  // it directly. No-op guard so the shared file stays portable to the Dawn port.
  function repaintAnsinBar() {
    if (typeof updateFreeShipping === "function") {
      try { updateFreeShipping(); } catch { /* non-fatal */ }
    }
  }

  // --- Cart writes --------------------------------------------------------
  // Read-merge-write cart attributes — clone of cart.js syncBundleNameAttribute:
  // GET /cart.js -> spread existing cart.attributes -> POST cart_update_url.
  // Never clobber siblings (bundle_name, snrs_uuid, ...). Requests the cart
  // sections alongside the write so the server-rendered bar repaints in place.
  async function postCartAttributes(attributes) {
    const targets = exchangeSectionTargets();
    const body = { attributes };
    if (targets.length) {
      body.sections = targets.map((t) => t.section).join(",");
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(routes.cart_update_url, {
      ...postConfig(),
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    let updated = null;
    try {
      updated = await res.clone().json();
    } catch { /* non-JSON — bar refreshes on next nav */ }
    if (targets.length && updated?.sections) injectSections(updated.sections, targets);
    repaintAnsinBar();
    // The updated cart, so callers can read the token it actually settled on.
    return updated;
  }

  /**
   * @returns {Promise<string|null>} The token of the cart the session was written
   *   onto. Recorded in the tab mirror so reconcile can later tell a cart that was
   *   EMPTIED (same token — repair) from one that was REPLACED by a completed
   *   checkout (new token — the exchange is over). See the reconcile repair branch.
   */
  async function writeExchangeAttributes(exchange) {
    try {
      const res = await fetch(`${routes.cart_url}.js`, { method: "GET", credentials: "same-origin" });
      const cart = await res.json();
      const nextAttributes = {
        ...(cart?.attributes ?? {}),
        _exchange_session: "1",
        _exchange_original_order: exchange.order,
        _exchange_deadline: String(exchange.deadline),
        _exchange_variant: exchange.variant,
        _exchange_items: exchange.items ?? "",
        _exchange_paid: exchange.paid ?? "",
      };
      // Store-local deadline for the Function's 2 h enforcement. Omitted (not
      // guessed) when the shop offset is unknown — the Function then falls back to
      // the day-granular epoch-ms check.
      const localDeadline = toShopLocalIso(exchange.deadline);
      if (localDeadline) nextAttributes._exchange_deadline_local = localDeadline;
      log("writing cart attributes", nextAttributes);
      const written = await postCartAttributes(nextAttributes);
      // Anchor on the token from the WRITE, not from the read above it. A visitor who
      // has not touched the cart yet has no persisted cart, and the token /cart.js
      // reports for that provisional one is not necessarily the token it keeps once
      // this write brings it into existence. Anchoring on the earlier value made the
      // token look "changed" on the very next page load.
      const token = written?.token ?? cart?.token ?? null;
      log("cart attributes written to cart", token);
      return token;
    } catch (e) {
      console.warn("[exchange-session] write failed:", e);
      return null;
    }
  }

  /**
   * Write this tab's mirror, carrying the cart token alongside the payload.
   *
   * @param {object} exchange
   * @param {string|null} cartToken
   */
  const storeMirror = (exchange, cartToken) => {
    setAnchor(cartToken);
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ ...exchange, cartToken: cartToken ?? null }));
    } catch { /* private mode */ }
  };

  // Remove the exchange marker from the CART (the carrier of record). Called when a
  // session lapses so the cart bar and the Function both stop granting — the design's
  // "exit": the cart is browser-wide, so clearing it here (not just sessionStorage)
  // is what actually flips the state. Setting attribute values to "" removes them.
  // Repaints the bar to the normal threshold state via the same section swap.
  async function clearCartExchangeAttributes() {
    try {
      await postCartAttributes({
        _exchange_session: "",
        _exchange_original_order: "",
        _exchange_deadline: "",
        _exchange_deadline_local: "",
        _exchange_variant: "",
        _exchange_items: "",
        _exchange_paid: "",
      });
      log("cleared exchange attributes from cart (lapsed)");
    } catch (e) {
      console.warn("[exchange-session] clear failed:", e);
    }
  }

  // Parse + sanitize the exchange payload from the current URL.
  // Treat every value as data only (numeric coercion) — never a DOM sink.
  // Returns null when absent, malformed, or already expired (now >= deadline).
  function parseExchangeFromUrl() {
    const params = new URLSearchParams(location.search);
    if (params.get("ex") !== "1") return null;
    const order = String(params.get("ex_order") ?? "").replace(/[^0-9]/g, "");
    // ex_variant: comma-separated variant ids. ex_items: "variantId:qty,..." — the
    // quantity-aware payload added when the account modal gained multi-select.
    // Both are sanitized to their own alphabets and treated as opaque data; they
    // are never a DOM sink.
    const variant = String(params.get("ex_variant") ?? "").replace(/[^0-9,]/g, "");
    const items = String(params.get("ex_items") ?? "").replace(/[^0-9:,]/g, "");
    // ex_paid: "<order>|<currency>|<productId>:<paid>:<qty>,..." — what the customer
    // paid for the pieces she sends back, read by the exchange-paid-price Function.
    // Sanitized to its own alphabet; opaque data, never a DOM sink.
    const paid = String(params.get("ex_paid") ?? "").replace(/[^0-9A-Z|:.,]/g, "");
    const deadline = Number(params.get("ex_deadline"));
    if (!order) {
      log("ignored landing: missing/blank ex_order");
      return null;
    }
    if (!Number.isFinite(deadline) || Date.now() >= deadline) {
      log("ignored landing: deadline missing or expired ->", params.get("ex_deadline"));
      return null;
    }
    return { order, variant, items, paid, deadline };
  }

  // --- Line item property injection (ALL payment methods) -----------------
  // Cart attributes ride cart -> checkout, but accelerated checkout (Buy Now /
  // Shop Pay / Apple Pay) submits the PRODUCT FORM and bypasses the cart, so the
  // cart attributes never reach those orders. Instead of hiding those buttons, we
  // carry the exchange context as PRIVATE line item properties injected into every
  // product form: `<input name="properties[_exchange_*]">`. These ride the line
  // item through EVERY path (add-to-cart, Buy Now, wallets), reach the order, and
  // are read by the Function per-line. The `_` prefix keeps them hidden in the cart
  // (theme filters them) and at checkout. All payment methods stay available.
  let exchangeFormPayload = null; // { order, variant, deadline } while active, else null
  let formObserver = null;
  let formSyncScheduled = false;

  const payloadFromAttrs = (attrs) => ({
    order: attrs._exchange_original_order,
    variant: attrs._exchange_variant,
    items: attrs._exchange_items,
    paid: attrs._exchange_paid,
    deadline: Number(attrs._exchange_deadline),
  });

  const productForms = () =>
    document.querySelectorAll('form[action*="/cart/add"]');

  // Idempotent: clear any previously injected inputs, then (re)add for the active
  // payload. Disconnects the observer around the DOM writes so our own mutations
  // don't retrigger it.
  function syncExchangeFormProperties() {
    if (formObserver) formObserver.disconnect();
    const payload = exchangeFormPayload;
    productForms().forEach((form) => {
      form.querySelectorAll("input[data-exchange-prop]").forEach((el) => el.remove());
      if (!payload) return;
      const fields = {
        _exchange_session: "1",
        _exchange_original_order: payload.order ?? "",
        _exchange_deadline: String(payload.deadline ?? ""),
        _exchange_variant: payload.variant ?? "",
        _exchange_items: payload.items ?? "",
        _exchange_paid: payload.paid ?? "",
      };
      // Parity with the cart attributes so an accelerated-checkout order (Buy Now /
      // Shop Pay / Apple Pay), which never touches the cart, still carries the
      // store-local deadline the Function prefers.
      const localDeadline = toShopLocalIso(Number(payload.deadline));
      if (localDeadline) fields._exchange_deadline_local = localDeadline;
      for (const [key, value] of Object.entries(fields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        input.value = value;
        input.setAttribute("data-exchange-prop", "1");
        form.appendChild(input);
      }
    });
    if (formObserver && payload) {
      formObserver.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function scheduleFormSync() {
    if (formSyncScheduled) return;
    formSyncScheduled = true;
    setTimeout(() => {
      formSyncScheduled = false;
      if (exchangeFormPayload) syncExchangeFormProperties();
    }, 0);
  }

  // Set (or clear with null) the active exchange payload and reflect it onto the
  // product forms. While active, a MutationObserver re-applies the inputs to any
  // form Shopify/theme re-renders (e.g. on variant change), so the marker is
  // present the instant the customer submits add-to-cart or an accelerated button.
  function setExchangeFormPayload(payload) {
    exchangeFormPayload = payload || null;
    if (!formObserver && typeof MutationObserver !== "undefined") {
      formObserver = new MutationObserver((mutations) => {
        if (!exchangeFormPayload) return;
        for (const m of mutations) {
          if (m.addedNodes && m.addedNodes.length) { scheduleFormSync(); return; }
        }
      });
    }
    syncExchangeFormProperties();
  }

  // --- Lapse --------------------------------------------------------------
  // THE single exit from a session, whether it is reached by the timer below, by
  // reconcile() on the next page load, by a bfcache restore, or by the customer
  // pressing the bar's end-session button. Clears the carrier of record, this tab's
  // mirror, the browser hint, the form payload, and both UI surfaces. Silent by
  // decision (no "expired" notice) — ANSIN 2026-08 confirmed the announcement bar
  // simply disappearing IS the signal.
  //
  // Order matters and is load-bearing: the cart write lands BEFORE the mirror is
  // dropped, so runReconcile() can never catch a cleared cart next to a live
  // sessionStorage payload and helpfully write the session back.
  async function lapseNow(reason) {
    clearLapseTimer();
    await clearCartExchangeAttributes();
    clearStoredExchange();
    clearHint();
    clearAnchor();
    setExchangeFormPayload(null);
    syncExchangeBar(null);
    repaintAnsinBar();
    log(`session ended (${reason || "lapsed"}) — cleared exchange attributes`);
  }

  // The manual exit the announcement bar's "Zakończ proces" button calls. Exposed
  // rather than wired up here because this file is theme-portable (the plusik Dawn
  // port shares it verbatim and has no announcement bar); the ANSIN-specific button
  // is bound in assets/exchange-bar.js.
  //
  // Anything reaching for this must use it rather than clearing the cart attributes
  // directly — a partial exit leaves the sessionStorage mirror behind, and the next
  // reconcile() restores the session from it.
  window.ANSIN_EXCHANGE_SESSION = {
    end: () => lapseNow("ended by customer"),
  };

  // --- Lapse timer --------------------------------------------------------
  // Before the 2 h session, expiry was only ever noticed on the next interaction
  // (page load, refocus, cart update). Over 14 days nobody saw that; over 2 h an
  // idle open tab would sit there advertising free shipping that checkout refuses.
  //
  // setTimeout caps at 2^31-1 ms and is throttled — sometimes frozen — in background
  // tabs, so never trust one long timer: chunk it and re-check the wall clock at
  // every hop. A genuinely frozen tab cannot fire at all; that case is covered by
  // the pageshow/visibilitychange reconcile, which converges on the same lapseNow().
  const MAX_TIMEOUT_MS = 2147483000;
  let lapseTimerId = null;

  function clearLapseTimer() {
    if (lapseTimerId !== null) {
      clearTimeout(lapseTimerId);
      lapseTimerId = null;
    }
  }

  function scheduleLapse(deadlineMs) {
    clearLapseTimer();
    const deadline = Number(deadlineMs);
    if (!Number.isFinite(deadline)) return;

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      void lapseNow();
      return;
    }
    // +250 ms absorbs an early fire from clock/rounding skew, which would otherwise
    // re-arm in a tight loop right at the boundary.
    lapseTimerId = setTimeout(() => {
      lapseTimerId = null;
      if (Date.now() >= deadline) void lapseNow();
      else scheduleLapse(deadline);
    }, Math.min(remaining + 250, MAX_TIMEOUT_MS));
  }

  // --- Capture ------------------------------------------------------------
  // Only fires on a landing that carries ?ex=1.
  async function capture() {
    const exchange = parseExchangeFromUrl();
    if (!exchange) return;
    log("captured payload", exchange, "deadline", new Date(exchange.deadline).toISOString());
    // Inject BEFORE the async write: this landing is a PDP where Buy Now bypasses
    // the cart, so the line item property is what carries the exchange context to
    // such an order. Doing it first means an immediate Buy Now still stamps the line.
    setExchangeFormPayload(exchange);
    // Same reasoning for the bar: do the synchronous UI work first so the exchange
    // bar is right even if the cart write is slow or fails outright.
    syncExchangeBar(exchange.deadline);
    setHint();
    const cartToken = await writeExchangeAttributes(exchange);
    scheduleLapse(exchange.deadline);
    storeMirror(exchange, cartToken);
    // Scrub params so a refresh/share does not re-trigger or leak the payload.
    history.replaceState({}, "", location.pathname);
    log("URL scrubbed to", location.pathname);
  }

  // --- Reconcile ----------------------------------------------------------
  // Keep-alive: page-load reconciliation is the REAL re-apply trigger.
  // cart.js removals / qty-changes mutate via routes.cart_change_url and DO NOT
  // publish PUB_SUB_EVENTS.cartUpdate, and the theme has no /cart/clear.js — so a
  // dropped marker is caught on the next page view, not by a cart event.
  // Serialized entry point. Two guards, both learned the hard way:
  //
  // 1. AWAIT THE CAPTURE. `pageshow` fires on every page load, not just bfcache
  //    restores, and it calls reconcile() directly — bypassing init()'s careful
  //    `await capture()` ordering. On a ?ex=1 landing that meant reconcile ran
  //    while capture's cart write was still in flight, fetched a cart that was not
  //    stamped yet, concluded there was no session, and tore it down: the bar
  //    appeared from the pre-paint hint and then vanished a moment later (visible
  //    layout shift), with the browser hint cleared for good measure.
  //
  // 2. RUN ONE AT A TIME. DOMContentLoaded, pageshow, visibilitychange and
  //    cartUpdate can all fire within the same tick. Overlapping runs each fetch
  //    /cart.js and then apply conclusions drawn from different snapshots.
  let reconcileChain = Promise.resolve();
  function reconcile() {
    reconcileChain = reconcileChain.then(async () => {
      await capturePromise;
      await runReconcile();
    });
    return reconcileChain;
  }

  async function runReconcile() {
    let exchange = getStoredExchange();
    // Lapse this tab's mirror if expired — clean lapse, no automatic re-issue.
    if (exchange && (!Number.isFinite(Number(exchange.deadline)) || Date.now() >= exchange.deadline)) {
      clearStoredExchange();
      exchange = null;
      log("session lapsed (deadline passed) — cleared, no re-issue");
    }
    // Fast path: this browser never touched the exchange flow, so there is no stamped
    // cart to consult. Keeps ordinary visitors off the /cart.js fetch entirely.
    //
    // But NEVER take it when the server already said otherwise. layout/theme.liquid
    // sets the root class from cart.attributes, so the class means "the cart, the
    // carrier of record, has a live session on it". Browser storage is derived state
    // and can be missing for plenty of innocent reasons — a fresh tab (sessionStorage
    // is per-tab), private mode, or a hint cleared by an earlier teardown. Hiding the
    // bar on that alone made the failure self-reinforcing: once the hint was gone,
    // every later page load hid a session that was genuinely still running.
    // Ordinary visitors have neither the hint nor the class, so they still skip the fetch.
    const serverSaysActive = (() => {
      try {
        return Boolean(window.ANSIN_EXCHANGE_BAR && window.ANSIN_EXCHANGE_BAR.isActive());
      } catch {
        return false;
      }
    })();

    if (!exchange && !hasHint() && !serverSaysActive) {
      setExchangeFormPayload(null);
      clearLapseTimer();
      syncExchangeBar(null);
      return;
    }
    if (serverSaysActive && !hasHint()) {
      // Re-arm the hint so subsequent loads consult the cart without this detour.
      setHint();
      log("root class says active but the browser hint was missing — restored");
    }
    try {
      const res = await fetch(`${routes.cart_url}.js`, { method: "GET", credentials: "same-origin" });
      const cart = await res.json();
      const attrs = cart?.attributes ?? {};
      const stamped = attrs._exchange_session === "1";
      const deadlineMs = Number(attrs._exchange_deadline);
      const hasReadableDeadline = Number.isFinite(deadlineMs);
      const expired = hasReadableDeadline && Date.now() >= deadlineMs;
      const active = stamped && hasReadableDeadline && !expired;

      // ORDER PLACED — checked BEFORE anything reads the marker.
      //
      // A session belongs to ONE cart. Completing checkout replaces that cart, and
      // the replacement arrives EMPTY BUT CARRYING THE OLD ATTRIBUTES FORWARD — the
      // `_exchange_*` marker is still sitting there on a cart the customer has
      // already paid out of. So the marker cannot be trusted to tell us the exchange
      // is over; only the token changes, every time.
      //
      // This first shipped inside the repair branch below, which is reached only when
      // the marker is ABSENT — so it never ran once, and the bar, the countdown and
      // the free shipping all survived the order (ANSIN 2026-08). It has to sit here,
      // above every branch that looks at `stamped`.
      //
      // Leaving it stamped is not cosmetic: the Function reads those same attributes,
      // so the customer's next order would have shipped free both ways too.
      // The anchor comes from localStorage FIRST and only falls back to this tab's
      // mirror. Reading it from the mirror alone was the reason this still failed:
      // sessionStorage is per tab, so a customer who checked out and came back in a
      // fresh tab — or anyone who cleared it — had no anchor to compare, and the
      // branch below rehydrated the mirror straight back off the stamped cart.
      const anchor = getAnchor() ?? exchange?.cartToken ?? null;
      if (anchor && cart?.token && anchor !== cart.token) {
        log("cart token changed — the session's cart is gone, ending", anchor, "->", cart.token);
        await lapseNow("cart replaced (order placed)");
        return;
      }

      // REACTIVE LAPSE: the cart still carries the marker AND we can see that its
      // deadline has passed. The cart is the carrier of record, so clear it here —
      // this is what makes the storefront react to expiry. Silent by decision
      // (no "expired" notice); ANSIN confirmed the bar disappearing IS the signal.
      //
      // Gate on `expired`, NOT on `!active`. They differ in one case that matters:
      // a cart stamped with _exchange_session=1 whose _exchange_deadline is missing
      // or unparseable is CORRUPT, not expired. Treating that as expiry ran the full
      // teardown — cart attributes, hint, tab mirror and the bar all wiped — so the
      // bar appeared from the server-rendered class and then vanished, permanently.
      // Corrupt state is repaired further down instead, from this tab's mirror.
      if (stamped && expired) {
        await lapseNow();
        return;
      }

      // Form-property injection follows the CART (browser-wide), plus this tab's
      // mirror during the re-apply window below. Deriving the payload from the cart
      // attributes when active means every tab stamps its product forms consistently,
      // so an order placed in a second tab still carries the exchange line property.
      setExchangeFormPayload(exchange || (active ? payloadFromAttrs(attrs) : null));

      // Follow the CART for both the bar and the lapse timer: it is the carrier of
      // record, so every tab converges on the same deadline.
      const liveDeadline = active ? deadlineMs : exchange?.deadline;
      if (active || exchange) {
        syncExchangeBar(liveDeadline);
        scheduleLapse(liveDeadline);
      }

      // REPAIR, don't tear down. Covers the marker being absent (Shopify drops cart
      // attributes in some transitions, e.g. around an emptied cart) and the marker
      // being present without a readable deadline. Either way this tab still holds a
      // valid mirror, so the fix is to write the session back.
      //
      // Reached only when the cart still belongs to this session — the token check
      // above already sent a replaced cart to lapseNow — so a missing marker here
      // really is a dropped attribute and not a completed order.
      if (exchange && (!stamped || !hasReadableDeadline)) {
        log("cart marker missing or unreadable — re-applying from this tab's mirror");
        const cartToken = await writeExchangeAttributes(exchange);
        // Anchor mirrors that predate the token, so they get the same protection from
        // the next reconcile onward. Sessions already in flight when this shipped are
        // the only ones without it, and they carry the old behaviour until they end.
        if (!exchange.cartToken && cartToken) storeMirror(exchange, cartToken);
        return;
      }
      if (active && !exchange) {
        // Rehydrate this tab's mirror from the carrier of record so keep-alive and
        // lapse behave identically in every tab.
        storeMirror(payloadFromAttrs(attrs), cart?.token ?? null);
        log("rehydrated session mirror from cart attributes");
        return;
      }
      // Nothing active in the cart and no mirror — stop consulting the cart.
      if (!active && !exchange) {
        clearHint();
        clearLapseTimer();
        syncExchangeBar(null);
      }
    } catch (e) {
      console.warn("[exchange-session] reconcile failed:", e);
    }
  }

  // --- Boot ---------------------------------------------------------------
  // Start the capture at script evaluation, NOT inside init(). This script is
  // deferred, so the DOM is parsed and the product forms exist; starting here means
  // the promise is already pending by the time `pageshow` fires, which is what lets
  // every reconcile() await it instead of racing it.
  //
  // ORDERING: this `const` must stay declared above every reconcile() CALL SITE
  // (init, pageshow, visibilitychange, cartUpdate) — reconcile awaits it, so calling
  // it earlier would hit the temporal dead zone. It cannot simply move higher
  // either: capture() reaches scheduleLapse(), which touches `lapseTimerId`.
  const capturePromise = (async () => {
    // `routes` always resolves (theme routes or the platform fallback), so there is
    // no hard dependency on the theme's globals being ready.
    if (!routes?.cart_url || !routes?.cart_update_url) {
      log("skipped: no cart routes");
      return;
    }
    // ?ex=1 landing writes fresh attributes (also the SESSION-03 re-click restart).
    await capture();
  })();

  async function init() {
    if (!routes?.cart_url || !routes?.cart_update_url) return;
    await capturePromise;
    await reconcile(); // every page load: re-apply if the marker was dropped, lapse if expired
    // Secondary trigger ONLY (add-to-cart re-render within a single page) — never
    // the sole re-apply path; the page-load reconcile above is authoritative.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, reconcile);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  // bfcache back/forward restores and tab refocus after a cart edit: reconcile
  // before the next navigation carries the customer to checkout.
  window.addEventListener("pageshow", reconcile);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") reconcile();
  });
})();
